import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorSecret, setTwoFactorSecret] = useState<string | null>(null);
  const [twoFactorQrCode, setTwoFactorQrCode] = useState<string | null>(null);
  const [emailVerified, setEmailVerified] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setEmailVerified(session?.user?.email_confirmed_at != null);
      setLoading(false);
      
      // Check if 2FA is enabled for this user
      if (session?.user) {
        checkTwoFactorStatus(session.user.id);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setEmailVerified(session?.user?.email_confirmed_at != null);
      setLoading(false);
      
      // Check if 2FA is enabled for this user
      if (session?.user) {
        checkTwoFactorStatus(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkTwoFactorStatus = async (userId: string) => {
    try {
      // Validate environment variables first
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseAnonKey || 
          supabaseUrl === 'your_supabase_url_here' || 
          supabaseAnonKey === 'your_supabase_anon_key_here') {
        console.warn('Supabase environment variables not configured, skipping 2FA check');
        return;
      }

      const supabaseFunctionsUrl = `${supabaseUrl}/functions/v1/manage-2fa`;
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const response = await fetch(supabaseFunctionsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          action: 'status',
          userId
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.warn('Error checking 2FA status:', errorData);
        return;
      }

      const result = await response.json();
      
      if (result.success) {
        setTwoFactorEnabled(result.isEnabled);
        if (result.hasSecret) {
          setTwoFactorSecret('SECRET_EXISTS_BUT_NOT_EXPOSED');
        } else {
          setTwoFactorSecret(null);
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.warn('2FA status check timed out');
      } else {
        console.warn('Error checking 2FA status:', error);
      }
    }
  };

  const signUp = async (email: string, password: string, referralCode?: string) => {
    try {
      // Create the user without email confirmation
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            referral_code: referralCode || null
          }
        }
      });

      if (error) {
        return { data: null, error };
      }

      // Send OTP email for verification
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
        
        if (!supabaseUrl || !supabaseAnonKey || 
            supabaseUrl === 'your_supabase_url_here' || 
            supabaseAnonKey === 'your_supabase_anon_key_here') {
          console.warn('Supabase environment variables not configured, skipping OTP email');
          return { 
            data: { 
              ...data, 
              emailVerificationSent: false 
            }, 
            error: null 
          };
        }

        const supabaseFunctionsUrl = `${supabaseUrl}/functions/v1/send-otp-email`;
        
        const response = await fetch(supabaseFunctionsUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseAnonKey}`,
          },
          body: JSON.stringify({ email }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Failed to send verification email' }));
          throw new Error(errorData.error || 'Failed to send verification email');
        }

        return { 
          data: { 
            ...data, 
            emailVerificationSent: true 
          }, 
          error: null 
        };
      } catch (otpError: any) {
        console.warn('Error sending OTP:', otpError);
        
        // Don't delete the user if OTP sending fails, just continue without verification
        return { 
          data: { 
            ...data, 
            emailVerificationSent: false 
          }, 
          error: null 
        };
      }
    } catch (error) {
      console.error('Error during sign up:', error);
      return { 
        data: { user: null, session: null },
        error: new Error('An error occurred during sign up')
      };
    }
  };

  const verifyEmail = async (email: string, otp: string) => {
    setVerifyingEmail(true);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseAnonKey || 
          supabaseUrl === 'your_supabase_url_here' || 
          supabaseAnonKey === 'your_supabase_anon_key_here') {
        throw new Error('Supabase environment variables not configured');
      }

      const supabaseFunctionsUrl = `${supabaseUrl}/functions/v1/verify-otp`;
      
      const response = await fetch(supabaseFunctionsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({ email, otp }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to verify email' }));
        throw new Error(errorData.error || 'Failed to verify email');
      }

      const result = await response.json();
      setEmailVerified(true);
      return { success: true, message: result.message };
    } catch (error: any) {
      console.error('Error verifying email:', error);
      return { success: false, error: error.message };
    } finally {
      setVerifyingEmail(false);
    }
  };

  const resendVerificationOtp = async (email: string) => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseAnonKey || 
          supabaseUrl === 'your_supabase_url_here' || 
          supabaseAnonKey === 'your_supabase_anon_key_here') {
        throw new Error('Supabase environment variables not configured');
      }

      const supabaseFunctionsUrl = `${supabaseUrl}/functions/v1/send-otp-email`;
      
      const response = await fetch(supabaseFunctionsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to resend verification email' }));
        throw new Error(errorData.error || 'Failed to resend verification email');
      }

      return { success: true, message: 'Verification email resent successfully' };
    } catch (error: any) {
      console.error('Error resending verification email:', error);
      return { success: false, error: error.message };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      // First check if 2FA is enabled for this user
      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('email', email)
        .maybeSingle();
      
      let is2faEnabledForUser = false;
      
      if (userData) {
        const { data: twoFactorData } = await supabase
          .from('user_2fa')
          .select('is_enabled, secret')
          .eq('user_id', userData.id)
          .maybeSingle();
        
        // Only set is2faEnabledForUser to true if we have valid data and is_enabled is true
        is2faEnabledForUser = twoFactorData?.is_enabled === true;
      }
      
      // If 2FA is enabled, return a special response indicating 2FA is required
      if (is2faEnabledForUser) {
        return { 
          data: { user: null, session: null, twoFactorRequired: true },
          error: null
        };
      }
      
      // If 2FA is not enabled, proceed with normal sign in
      return await supabase.auth.signInWithPassword({
        email,
        password,
      });
    } catch (error) {
      console.error('Error during sign in:', error);
      return { 
        data: { user: null, session: null },
        error: new Error('An error occurred during sign in')
      };
    }
  };

  // New function to complete sign-in with 2FA
  const completeSignInWith2FA = async (email: string, password: string, twoFactorCode: string) => {
    try {
      // First, verify the 2FA code
      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('email', email)
        .maybeSingle();
      
      if (!userData) {
        return { 
          data: { user: null, session: null },
          error: new Error('User not found')
        };
      }
      
      // Verify the 2FA code using the Edge Function
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
        
        if (!supabaseUrl || !supabaseAnonKey || 
            supabaseUrl === 'your_supabase_url_here' || 
            supabaseAnonKey === 'your_supabase_anon_key_here') {
          console.warn('Supabase environment variables not configured, skipping 2FA verification');
        } else {
          const supabaseFunctionsUrl = `${supabaseUrl}/functions/v1/manage-2fa`;
          
          const response = await fetch(supabaseFunctionsUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${supabaseAnonKey}`,
            },
            body: JSON.stringify({
              action: 'verify',
              userId: userData.id,
              token: twoFactorCode
            }),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Invalid two-factor authentication code' }));
            throw new Error(errorData.error || 'Invalid two-factor authentication code');
          }
          
          // If 2FA verification is successful, proceed with sign in
          const signInResult = await supabase.auth.signInWithPassword({
            email,
            password,
          });
          
          return signInResult;
        }
      } catch (verifyError) {
        console.error('Error verifying 2FA code:', verifyError);
        return { 
          data: { user: null, session: null },
          error: new Error('Invalid two-factor authentication code')
        };
      }
      
      // If we get here without returning, something went wrong
      return { 
        data: { user: null, session: null },
        error: new Error('Failed to complete authentication')
      };
    } catch (error) {
      console.error('Error during 2FA sign in:', error);
      return { 
        data: { user: null, session: null },
        error: new Error('An error occurred during sign in')
      };
    }
  };

  const signOut = async () => {
    return await supabase.auth.signOut();
  };

  const updatePassword = async (newPassword: string) => {
    return await supabase.auth.updateUser({
      password: newPassword,
    });
  };

  const resetPasswordForEmail = async (email: string) => {
    return await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
  };
  
  const setupTwoFactor = async () => {
    if (!user) return { error: new Error('User not authenticated') };
    
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseAnonKey || 
          supabaseUrl === 'your_supabase_url_here' || 
          supabaseAnonKey === 'your_supabase_anon_key_here') {
        throw new Error('Supabase environment variables not configured');
      }

      const supabaseFunctionsUrl = `${supabaseUrl}/functions/v1/manage-2fa`;
      
      const response = await fetch(supabaseFunctionsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          action: 'setup',
          userId: user.id
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to set up 2FA' }));
        throw new Error(errorData.error || 'Failed to set up 2FA');
      }

      const result = await response.json();
      
      if (result.success) {
        setTwoFactorSecret(result.secret);
        setTwoFactorQrCode(result.qrCode);
        
        return { 
          secret: result.secret,
          qrCode: result.qrCode
        };
      } else {
        throw new Error(result.error || 'Failed to set up 2FA');
      }
    } catch (error) {
      console.error('Error setting up 2FA:', error);
      return { error };
    }
  };
  
  const verifyAndEnableTwoFactor = async (token: string) => {
    if (!user) {
      return { error: new Error('User not authenticated') };
    }
    
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseAnonKey || 
          supabaseUrl === 'your_supabase_url_here' || 
          supabaseAnonKey === 'your_supabase_anon_key_here') {
        throw new Error('Supabase environment variables not configured');
      }

      const supabaseFunctionsUrl = `${supabaseUrl}/functions/v1/manage-2fa`;
      
      const response = await fetch(supabaseFunctionsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          action: 'verify',
          userId: user.id,
          token
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to verify code' }));
        throw new Error(errorData.error || 'Failed to verify code');
      }

      const result = await response.json();
      
      if (result.success) {
        // Immediately update the UI state to reflect the change
        setTwoFactorEnabled(true);
        return { success: true };
      } else {
        throw new Error(result.error || 'Failed to verify code');
      }
    } catch (error) {
      console.error('Error enabling 2FA:', error);
      return { error };
    }
  };
  
  const disableTwoFactor = async (token: string) => {
    if (!user) {
      return { error: new Error('User not authenticated') };
    }
    
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseAnonKey || 
          supabaseUrl === 'your_supabase_url_here' || 
          supabaseAnonKey === 'your_supabase_anon_key_here') {
        throw new Error('Supabase environment variables not configured');
      }

      const supabaseFunctionsUrl = `${supabaseUrl}/functions/v1/manage-2fa`;
      
      const response = await fetch(supabaseFunctionsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          action: 'disable',
          userId: user.id,
          token
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to disable 2FA' }));
        throw new Error(errorData.error || 'Failed to disable 2FA');
      }

      const result = await response.json();
      
      if (result.success) {
        // Immediately update the UI state to reflect the change
        setTwoFactorEnabled(false);
        setTwoFactorSecret(null);
        return { success: true };
      } else {
        throw new Error(result.error || 'Failed to disable 2FA');
      }
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      return { error };
    }
  };

  return {
    user,
    session,
    loading,
    emailVerified,
    verifyingEmail,
    twoFactorEnabled,
    twoFactorSecret,
    twoFactorQrCode,
    signUp,
    signIn,
    completeSignInWith2FA,
    signOut,
    updatePassword,
    resetPasswordForEmail,
    setupTwoFactor,
    verifyAndEnableTwoFactor,
    disableTwoFactor,
    verifyEmail,
    resendVerificationOtp
  };
};